import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import communityService from '@/services/communityService';
import { mabarService } from '@/services/mabarService';
import { CommunityEventsSkeleton } from '@/components/client/skeleton/ClientSkeletons';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/Button';

// Helper function to format Date Box (Month, Day, DayOfWeek)
const parseDateBox = (dateStr?: string) => {
  if (!dateStr) return { month: 'Jul', day: '1', dayName: 'Sen' };
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return { month: 'Jul', day: '1', dayName: 'Sen' };
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];
    const days = ['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'];
    return {
      month: months[d.getMonth()],
      day: String(d.getDate()),
      dayName: days[d.getDay()],
    };
  } catch {
    return { month: 'Jul', day: '1', dayName: 'Sen' };
  }
};

// Helper function to check if an event is Sparing
const checkIsSparring = (event: any): boolean => {
  if (!event) return false;
  let cfg = event.config;
  if (typeof cfg === 'string') {
    try {
      cfg = JSON.parse(cfg);
    } catch {}
  }

  if (cfg?.is_sparring !== undefined && cfg?.is_sparring !== null) {
    return cfg.is_sparring === true || cfg.is_sparring === 'true';
  }
  if (cfg?.is_sparing !== undefined && cfg?.is_sparing !== null) {
    return cfg.is_sparing === true || cfg.is_sparing === 'true';
  }
  if (event.is_sparring !== undefined && event.is_sparring !== null) {
    return event.is_sparring === true || event.is_sparring === 'true';
  }
  if (event.is_sparing !== undefined && event.is_sparing !== null) {
    return event.is_sparing === true || event.is_sparing === 'true';
  }

  return event.type === 'sparing' || event.type === 'sparring' || event.event_type === 'sparing' || event.event_type === 'sparring';
};

type EventTab = 'all' | 'mabar' | 'sparing';

const ClientCommunityEventsPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<EventTab>('all');
  const [events, setEvents] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(true);
  const [hostId, setHostId] = useState<number | null>(null);

  const limit = 6;

  // Scroll to top on mount, page, or tab change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    const mainElement = document.querySelector('main');
    if (mainElement) {
      mainElement.scrollTop = 0;
    }
  }, [page, activeTab]);

  // Fetch host ID from community profile
  useEffect(() => {
    const fetchCommunityInfo = async () => {
      if (!slug) return;
      try {
        const res = await communityService.getPublicProfile(slug);
        if (res.success && res.data) {
          const comm = res.data.community;
          const hId = res.data.mabar_user_id || comm?.community_user_id || comm?.id;
          if (hId) setHostId(hId);
        }
      } catch (err) {
        console.error('Failed to fetch community profile:', err);
      }
    };

    fetchCommunityInfo();
  }, [slug]);

  // Fetch events list on hostId, page, or activeTab change
  useEffect(() => {
    const fetchEventsList = async () => {
      if (!hostId) return;
      try {
        setLoading(true);
        const eventsRes = await mabarService.getPublicEvents({
          host_id: hostId,
          page,
          limit,
          type: activeTab !== 'all' ? activeTab : undefined,
        });

        const rawEvents = eventsRes?.data || [];
        setEvents(rawEvents);
        setTotalCount(eventsRes?.meta?.total || rawEvents.length);
      } catch (err) {
        console.error('Failed to fetch community events:', err);
        setEvents([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    };

    fetchEventsList();
  }, [hostId, page, activeTab]);

  const totalPages = Math.ceil(totalCount / limit) || 1;
  const startItem = totalCount > 0 ? (page - 1) * limit + 1 : 0;
  const endItem = Math.min(page * limit, totalCount);

  // Helper for pagination page numbers array
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (page > 3) pages.push('...');
      const start = Math.max(2, page - 1);
      const end = Math.min(totalPages - 1, page + 1);
      for (let i = start; i <= end; i++) {
        if (!pages.includes(i)) pages.push(i);
      }
      if (page < totalPages - 2) pages.push('...');
      if (!pages.includes(totalPages)) pages.push(totalPages);
    }
    return pages;
  };

  const handleTabChange = (tab: EventTab) => {
    if (activeTab === tab) return;
    setActiveTab(tab);
    setPage(1); // Reset to page 1 on tab change
  };

  return (
    <div className="bg-[#F8FAFC] min-h-screen pb-12 font-sans">
      <div className="max-w-md mx-auto px-4 pt-4 space-y-4">
        {/* Filter Pills Tabs (Semua / Mabar / Sparing) */}
        <div className="bg-[#F5F6F7] p-1 rounded-2xl flex items-center justify-between gap-1 text-xs font-semibold text-gray-500">
          <button
            type="button"
            onClick={() => handleTabChange('all')}
            className={`flex-1 py-2 rounded-xl transition-all cursor-pointer ${
              activeTab === 'all'
                ? 'bg-white text-[#FC480D] shadow-2xs font-bold'
                : 'hover:text-[#0E1726]'
            }`}
          >
            Semua
          </button>
          <button
            type="button"
            onClick={() => handleTabChange('mabar')}
            className={`flex-1 py-2 rounded-xl transition-all cursor-pointer ${
              activeTab === 'mabar'
                ? 'bg-white text-[#FC480D] shadow-2xs font-bold'
                : 'hover:text-[#0E1726]'
            }`}
          >
            Mabar
          </button>
          <button
            type="button"
            onClick={() => handleTabChange('sparing')}
            className={`flex-1 py-2 rounded-xl transition-all cursor-pointer ${
              activeTab === 'sparing'
                ? 'bg-white text-[#FC480D] shadow-2xs font-bold'
                : 'hover:text-[#0E1726]'
            }`}
          >
            Sparing
          </button>
        </div>

        {loading ? (
          <CommunityEventsSkeleton />
        ) : (
          <>
            {/* Count Info Bar */}
            <div className="flex items-center justify-between text-xs px-1">
              <span className="text-gray-400 font-medium">
                Menampilkan {startItem}–{endItem} dari {totalCount}
              </span>
              <span className="text-[#FC480D] font-extrabold">{totalCount} event</span>
            </div>

            {/* Event List */}
            {events.length === 0 ? (
              <Card className="p-8 text-center space-y-2">
                <Icon icon="ph:calendar-blank" className="w-10 h-10 text-gray-300 mx-auto" />
                <p className="text-xs text-gray-400 font-medium">
                  Belum ada event {activeTab === 'sparing' ? 'sparing' : activeTab === 'mabar' ? 'mabar' : ''} untuk komunitas ini
                </p>
              </Card>
            ) : (
              <div className="space-y-3.5">
                {events.map((event: any, idx: number) => {
                  const dateBox = parseDateBox(event.event_date || event.date);
                  const isSparing = checkIsSparring(event);

                  const locationStr = event.lokasi_arena || event.location || event.city || 'GOR Senayan';
                  const playerCount =
                    event.participants_count ||
                    event.participants?.length ||
                    event.total_participants ||
                    12;

                  return (
                    <Card
                      key={event.slug || event.id || idx}
                      onClick={() => navigate(`/event/${event.slug}`)}
                      className="p-4 space-y-3 hover:shadow-xs transition-all cursor-pointer"
                    >
                      <div className="flex items-start gap-3.5">
                        {/* Date Box */}
                        <div className="w-14 h-16 rounded-2xl bg-orange-50/80 border border-orange-100/60 p-1.5 flex flex-col items-center justify-center shrink-0 text-center">
                          <span className="text-[10px] font-bold text-[#FC480D] leading-none">
                            {dateBox.month}
                          </span>
                          <span className="text-lg font-extrabold text-[#0E1726] leading-snug">
                            {dateBox.day}
                          </span>
                          <span className="text-[10px] font-medium text-gray-400 leading-none">
                            {dateBox.dayName}
                          </span>
                        </div>

                        {/* Title & Info */}
                        <div className="min-w-0 flex-1 space-y-1">
                          <h3 className="text-sm font-extrabold text-[#0E1726] truncate">
                            {event.name || event.title}
                          </h3>
                          <p className="text-xs text-gray-400 font-medium flex items-center gap-1 truncate">
                            <Icon icon="ph:map-pin" className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                            <span className="truncate">{locationStr}</span>
                          </p>
                          <p className="text-xs text-gray-400 font-medium flex items-center gap-1 truncate">
                            <Icon icon="ph:users-three" className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                            <span>{playerCount} pemain hadir</span>
                          </p>
                        </div>
                      </div>

                      {/* Card Footer */}
                      <div className="flex items-center justify-between pt-1 border-t border-gray-50">
                        {/* Pill Tag */}
                        {isSparing ? (
                          <Badge variant="blue" className="py-1 px-3 text-[11px] font-bold gap-1.5">
                            <Icon icon="ph:shield-bold" className="w-3.5 h-3.5" />
                            <span>Sparing</span>
                          </Badge>
                        ) : (
                          <Badge variant="orange" className="py-1 px-3 text-[11px] font-bold gap-1.5">
                            <Icon icon="ph:users-three-bold" className="w-3.5 h-3.5" />
                            <span>Mabar</span>
                          </Badge>
                        )}

                        {/* Detail Button */}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/event/${event.slug}`);
                          }}
                          className="rounded-full px-3.5 py-1.5 h-auto text-xs font-bold gap-1"
                        >
                          <Icon icon="ph:caret-right-bold" className="w-3.5 h-3.5" />
                          <span>Lihat Detail</span>
                        </Button>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}

            {/* Pagination Bar */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 pt-2 pb-6">
                {/* Prev Button */}
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="w-8 h-8 rounded-full border border-gray-200 bg-white p-0 flex items-center justify-center text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors shadow-2xs cursor-pointer"
                >
                  <Icon icon="ph:caret-left-bold" className="w-4 h-4" />
                </Button>

                {/* Page Numbers */}
                {getPageNumbers().map((p, idx) =>
                  p === '...' ? (
                    <span key={`dots-${idx}`} className="text-gray-400 text-xs px-1">
                      ...
                    </span>
                  ) : (
                    <Button
                      key={`page-${p}`}
                      type="button"
                      variant={page === p ? "default" : "outline"}
                      size="icon"
                      onClick={() => setPage(p as number)}
                      className={`w-8 h-8 rounded-full text-xs font-bold transition-colors cursor-pointer p-0 flex items-center justify-center ${
                        page === p
                          ? 'bg-[#FC480D] text-white shadow-2xs hover:bg-[#D83B08]'
                          : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {p}
                    </Button>
                  )
                )}

                {/* Next Button */}
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="w-8 h-8 rounded-full border border-gray-200 bg-white p-0 flex items-center justify-center text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors shadow-2xs cursor-pointer"
                >
                  <Icon icon="ph:caret-right-bold" className="w-4 h-4" />
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ClientCommunityEventsPage;
